interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical: string;
}

type SEOContent = {
  [key: string]: SEOConfig;
};

export const seoContent: SEOContent = {
  // Main Pages
  '/': {
    title: 'NearSmarter - Expert Nearshore Software Teams | Powered by Luby',
    description: 'Hire your dream tech team with vetted nearshore software developers. 20+ years of banking-grade quality, AI-powered matching, and teams ready in 48 hours. Start now.',
    keywords: 'nearshore software development, software outsourcing, tech team staffing, remote developers, Luby',
    canonical: '/'
  },
  '/how-it-works': {
    title: 'How It Works - Fast Team Assembly Process | NearSmarter',
    description: 'Discover our streamlined 5-step process: Submit briefing, get validated candidates, interview, and start working in days. Simple, fast, and reliable.',
    keywords: 'team assembly, hiring process, software team hiring, tech recruitment',
    canonical: '/how-it-works'
  },
  '/how-it-works/hybrid-delivery': {
    title: 'Hybrid Delivery Model - Flexible Team Integration | NearSmarter',
    description: 'Seamlessly integrate nearshore talent with your in-house team. Flexible collaboration models designed for modern distributed teams.',
    keywords: 'hybrid delivery, distributed teams, remote collaboration, agile teams',
    canonical: '/how-it-works/hybrid-delivery'
  },
  '/how-it-works/ai-sourcing': {
    title: 'AI-Powered Sourcing - Smart Talent Matching | NearSmarter',
    description: 'Our AI-driven platform matches you with pre-vetted candidates in hours. Advanced algorithms ensure perfect fit for your technical requirements.',
    keywords: 'AI recruitment, talent matching, automated sourcing, tech hiring',
    canonical: '/how-it-works/ai-sourcing'
  },
  '/about': {
    title: 'About NearSmarter - 23 Years of Software Excellence | Powered by Luby',
    description: 'Backed by Luby\'s 23-year legacy in financial software. Trusted by Fortune 500 companies for banking-grade quality and reliable delivery.',
    keywords: 'about nearsmarter, luby software, company history, tech outsourcing',
    canonical: '/about'
  },
  '/about/story': {
    title: 'Our Story - NearSmarter Journey | Powered by Luby',
    description: 'From Brazilian fintech pioneer to global nearshore leader. Learn how 23 years of innovation shaped our mission to connect world-class talent.',
    keywords: 'company story, nearsmarter history, luby background',
    canonical: '/about/story'
  },
  '/about/leadership': {
    title: 'Leadership Team - Meet Our Executives | NearSmarter',
    description: 'Meet the experienced leaders driving NearSmarter\'s vision. Decades of combined expertise in software development and financial services.',
    keywords: 'leadership team, executives, management, company leaders',
    canonical: '/about/leadership'
  },
  '/about/careers': {
    title: 'Careers at NearSmarter - Join Our Team',
    description: 'Join a team that values innovation, quality, and growth. Explore career opportunities in software development, recruitment, and operations.',
    keywords: 'careers, jobs, employment, work at nearsmarter',
    canonical: '/about/careers'
  },
  '/contact': {
    title: 'Contact Us - Get Your Custom Team Quote | NearSmarter',
    description: 'Ready to hire your team? Contact us for a free consultation and custom quote. Get responses within 24 hours from our expert team.',
    keywords: 'contact, get quote, consultation, reach us',
    canonical: '/contact'
  },
  '/contact/book-call': {
    title: 'Book a Call - Schedule Your Consultation | NearSmarter',
    description: 'Schedule a free 30-minute consultation with our team. Discuss your project needs and get expert recommendations for your tech team.',
    keywords: 'book call, schedule meeting, consultation, demo',
    canonical: '/contact/book-call'
  },

  // Expertise Pages
  '/stack-selection/infrastructure': {
    title: 'Infrastructure Engineers - DevOps, Cloud & System Experts',
    description: 'Hire expert DevOps engineers, cloud architects, and infrastructure specialists. AWS, Azure, GCP, Kubernetes, Terraform expertise available now.',
    keywords: 'devops engineers, cloud architects, infrastructure, kubernetes, terraform, AWS, Azure',
    canonical: '/stack-selection/infrastructure'
  },
  '/stack-selection/development': {
    title: 'Software Developers - Full Stack, Backend & Frontend Experts',
    description: 'Build with top full-stack, backend, and frontend developers. React, Node.js, Python, Java, .NET expertise. Start in 48 hours.',
    keywords: 'software developers, full stack, backend, frontend, react, nodejs, python',
    canonical: '/stack-selection/development'
  },
  '/stack-selection/qa': {
    title: 'QA Engineers - Test Automation & Quality Assurance Experts',
    description: 'Ensure quality with expert QA engineers and test automation specialists. Selenium, Cypress, performance testing, and manual QA expertise.',
    keywords: 'qa engineers, test automation, quality assurance, selenium, testing',
    canonical: '/stack-selection/qa'
  },
  '/stack-selection/cybersecurity': {
    title: 'Cybersecurity Experts - Security Engineers & Analysts',
    description: 'Protect your systems with certified security engineers and analysts. Penetration testing, compliance, threat detection expertise available.',
    keywords: 'cybersecurity, security engineers, penetration testing, compliance, infosec',
    canonical: '/stack-selection/cybersecurity'
  },
  '/stack-selection/artificial-intelligence': {
    title: 'AI/ML Engineers - Machine Learning & Data Science Experts',
    description: 'Build intelligent systems with AI/ML engineers and data scientists. TensorFlow, PyTorch, NLP, computer vision expertise ready to deploy.',
    keywords: 'AI engineers, machine learning, data science, tensorflow, pytorch, NLP',
    canonical: '/stack-selection/artificial-intelligence'
  },
  '/stack-selection/product-management': {
    title: 'Product Managers - Agile & Product Strategy Experts',
    description: 'Drive product success with experienced product managers. Agile methodologies, roadmap planning, stakeholder management expertise.',
    keywords: 'product managers, agile, product strategy, roadmap, scrum',
    canonical: '/stack-selection/product-management'
  },
  '/stack-selection/ui-ux': {
    title: 'UI/UX Designers - User Experience & Interface Design Experts',
    description: 'Create exceptional user experiences with expert UI/UX designers. Figma, user research, prototyping, and design systems expertise.',
    keywords: 'ui ux designers, user experience, interface design, figma, prototyping',
    canonical: '/stack-selection/ui-ux'
  },
  '/stack-selection/project-management': {
    title: 'Project Managers - Scrum Masters & Delivery Leads',
    description: 'Ensure on-time delivery with certified project managers and scrum masters. PMP, CSM, agile delivery expertise for your projects.',
    keywords: 'project managers, scrum masters, agile, PMP, delivery management',
    canonical: '/stack-selection/project-management'
  },
  '/stack-selection/data': {
    title: 'Data Engineers - Analytics, ETL & Data Architecture Experts',
    description: 'Build robust data pipelines with expert data engineers. Snowflake, dbt, Airflow, data warehousing, and ETL expertise available.',
    keywords: 'data engineers, data analytics, ETL, data architecture, snowflake, dbt',
    canonical: '/stack-selection/data'
  },

  // Industry Solutions
  '/solutions/fintech': {
    title: 'Fintech Software Teams - Banking & Payment Solutions',
    description: 'Expert fintech development teams with 23 years of banking experience. PCI DSS compliant, core banking, payment processing expertise.',
    keywords: 'fintech development, banking software, payment solutions, PCI DSS, financial services',
    canonical: '/solutions/fintech'
  },
  '/solutions/healthcare': {
    title: 'Healthcare Software Development - HIPAA Compliant Teams',
    description: 'HIPAA-compliant healthcare software teams. EHR integration, telemedicine, medical device software, and health tech expertise.',
    keywords: 'healthcare software, HIPAA compliant, EHR, telemedicine, health tech',
    canonical: '/solutions/healthcare'
  },
  '/solutions/ecommerce': {
    title: 'E-commerce Development Teams - Online Retail Solutions',
    description: 'Scale your online business with expert e-commerce developers. Shopify, Magento, custom platforms, payment integration expertise.',
    keywords: 'ecommerce development, online retail, shopify, magento, payment integration',
    canonical: '/solutions/ecommerce'
  },
  '/solutions/education': {
    title: 'EdTech Software Teams - Education Technology Solutions',
    description: 'Transform education with expert EdTech developers. LMS platforms, student portals, online learning, and educational app expertise.',
    keywords: 'edtech, education software, LMS, online learning, educational technology',
    canonical: '/solutions/education'
  },
  '/solutions/manufacturing': {
    title: 'Manufacturing Software - Industry 4.0 Solutions',
    description: 'Modernize manufacturing with Industry 4.0 software teams. IoT, automation, MES, supply chain, and smart factory expertise.',
    keywords: 'manufacturing software, industry 4.0, IoT, automation, MES, smart factory',
    canonical: '/solutions/manufacturing'
  },
  '/solutions/real-estate': {
    title: 'Real Estate Software - PropTech Development Teams',
    description: 'Innovate real estate with PropTech developers. Property management, CRM, listing platforms, and real estate marketplace expertise.',
    keywords: 'proptech, real estate software, property management, real estate CRM',
    canonical: '/solutions/real-estate'
  },
  '/solutions/logistics': {
    title: 'Logistics Software - Supply Chain Solutions',
    description: 'Optimize logistics with expert supply chain developers. TMS, WMS, route optimization, tracking, and fleet management expertise.',
    keywords: 'logistics software, supply chain, TMS, WMS, fleet management, tracking',
    canonical: '/solutions/logistics'
  },
  '/solutions/media': {
    title: 'Media & Entertainment Software Development Teams',
    description: 'Create engaging media platforms with expert developers. Streaming, content management, social platforms, and media tech expertise.',
    keywords: 'media software, entertainment tech, streaming, content management, video platforms',
    canonical: '/solutions/media'
  },
  '/solutions/government': {
    title: 'Government Software - GovTech Solutions',
    description: 'Modernize government services with secure GovTech teams. Citizen portals, compliance, data security, and public sector expertise.',
    keywords: 'govtech, government software, public sector, citizen services, compliance',
    canonical: '/solutions/government'
  },
  '/solutions/startups': {
    title: 'Startup Software Teams - MVP & Product Development',
    description: 'Launch faster with startup-focused development teams. MVP development, rapid prototyping, scalable architecture expertise.',
    keywords: 'startup development, MVP, rapid prototyping, product development, tech startups',
    canonical: '/solutions/startups'
  },

  // Why/Insights Pages
  '/why/powered-by-luby': {
    title: 'Powered by Luby - 23 Years of Software Excellence',
    description: 'Backed by Luby\'s proven track record in financial services. 23 years delivering banking-grade software to Fortune 500 companies.',
    keywords: 'luby software, banking software, financial services, enterprise development',
    canonical: '/why/powered-by-luby'
  },
  '/why/banking-quality': {
    title: 'Banking-Grade Quality - Financial Services Expertise',
    description: 'Experience banking-level quality standards. Security, compliance, and reliability proven in financial services for over two decades.',
    keywords: 'banking quality, financial software, compliance, security standards',
    canonical: '/why/banking-quality'
  },
  '/why/track-record': {
    title: 'Track Record - Proven Success Stories | NearSmarter',
    description: 'Proven results across industries. See how we\'ve helped companies scale with reliable nearshore teams and banking-grade delivery.',
    keywords: 'success stories, case studies, client results, proven track record',
    canonical: '/why/track-record'
  },
  '/insights': {
    title: 'Insights - Latest Blog Posts & Articles | NearSmarter',
    description: 'Stay informed with the latest insights, trends, and best practices — curated by our experts. Expert insights on software development, team hiring, and tech trends.',
    keywords: 'tech blog, software development articles, tech insights, industry trends, blog posts',
    canonical: '/insights'
  },
  '/insights/client-success': {
    title: 'Client Success Stories - Case Studies | NearSmarter',
    description: 'Real results from real clients. Explore case studies showcasing successful team integrations and project deliveries across industries.',
    keywords: 'case studies, client success, testimonials, client stories',
    canonical: '/insights/client-success'
  },
  '/insights/articles': {
    title: 'Articles & Insights - Software Development Blog | NearSmarter',
    description: 'Expert insights on software development, team hiring, and tech trends. Learn from 23 years of industry experience.',
    keywords: 'tech blog, software development articles, tech insights, industry trends',
    canonical: '/insights/articles'
  },

  // Application Pages
  '/stack-selection': {
    title: 'Hire Your Team - Select Expert Positions | NearSmarter',
    description: 'Choose from 50+ expert positions across development, design, QA, and more. Hire your custom tech team in minutes.',
    keywords: 'team builder, hire developers, tech positions, custom team',
    canonical: '/stack-selection'
  },
  '/calculator': {
    title: 'Team Cost Calculator - Get Instant Pricing | NearSmarter',
    description: 'Calculate your team cost instantly. Transparent pricing for all seniority levels. Get your custom quote in seconds.',
    keywords: 'cost calculator, pricing, team cost, developer rates',
    canonical: '/calculator'
  },
  '/summary': {
    title: 'My Squad Summary - Team Configuration | NearSmarter',
    description: 'Review your custom team configuration. Finalize roles, skills, and project details before submission.',
    keywords: 'team summary, squad configuration, project details',
    canonical: '/summary'
  },
  '/briefing': {
    title: 'Final Briefing - Project Requirements | NearSmarter',
    description: 'Complete your project briefing. Schedule interviews and finalize requirements for your custom tech team.',
    keywords: 'project briefing, requirements, interview scheduling',
    canonical: '/briefing'
  },
  '/my-squads': {
    title: 'My Squads - Manage Your Teams | NearSmarter',
    description: 'View and manage all your active squads. Track team configurations, project status, and team members.',
    keywords: 'manage teams, active squads, team dashboard',
    canonical: '/my-squads'
  },
  '/share-estimate': {
    title: 'Thank You - Squad Submitted Successfully | NearSmarter',
    description: 'Your squad request has been submitted! Our team will contact you within 24 hours to discuss next steps.',
    keywords: 'thank you, submission confirmed, next steps',
    canonical: '/share-estimate'
  },
  '/thank-you': {
    title: 'Thank You - NearSmarter',
    description: 'Thank you for completing your briefing. The NearSmarter team has been notified and you will receive a confirmation email shortly.',
    keywords: 'thank you, appreciation, briefing complete',
    canonical: '/thank-you'
  },

  // Auth/Legal Pages
  '/auth': {
    title: 'Sign In - Access Your Account | NearSmarter',
    description: 'Sign in to manage your squads, view estimates, and track your team hiring progress.',
    keywords: 'sign in, login, account access, authentication',
    canonical: '/auth'
  },
  '/security-compliance': {
    title: 'Security & Compliance - Data Protection Standards',
    description: 'Learn about our security practices and compliance certifications. SOC 2, ISO 27001, GDPR compliant operations.',
    keywords: 'security, compliance, data protection, SOC 2, ISO 27001, GDPR',
    canonical: '/security-compliance'
  },
  '/terms-of-service': {
    title: 'Terms of Service - Legal Information | NearSmarter',
    description: 'Read our terms of service and legal agreements. Understand your rights and responsibilities when using NearSmarter.',
    keywords: 'terms of service, legal, terms and conditions, user agreement',
    canonical: '/terms-of-service'
  },

  // Additional Pages
  '/cart-sync': {
    title: 'Syncing Your Squad - Please Wait | NearSmarter',
    description: 'We\'re syncing your squad configuration. This will only take a moment.',
    keywords: 'loading, syncing, cart sync',
    canonical: '/cart-sync'
  },
  '/company-registration': {
    title: 'Company Registration - Complete Your Profile | NearSmarter',
    description: 'Complete your company profile to get personalized team recommendations and pricing.',
    keywords: 'company registration, profile setup, onboarding',
    canonical: '/company-registration'
  },
  '/404': {
    title: 'Page Not Found - 404 Error | NearSmarter',
    description: 'The page you\'re looking for doesn\'t exist. Return to our homepage to explore our services.',
    keywords: '404, page not found, error',
    canonical: '/404'
  }
};

